import {useAccountExitGuard} from '../hooks/useUnsavedChanges';
import RentalProgress from './RentalProgress';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRefData } from "../context/RefDataContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { StatusBadge, DeadlineBadge } from "./StatusBadge";
import {can,canAdministerCompany,canOpenRoute} from "../lib/permissions";
import {downloadAuthorized} from "../lib/download";
import { fmtEUR, fmtDate, labelize } from "../lib/format";
import { printCell, printTable } from "../lib/print";
import { Plus, Pencil, Trash2, Search, Download, Loader2, ArrowUpDown, Inbox, ArrowLeftRight, Printer } from "lucide-react";
import OperationalLink from "./OperationalLink";
import EmptyState from "./EmptyState";
import ClientPicker from "./ClientPicker";
import ListSkeleton from "./ListSkeleton";
import useRememberedState from "../hooks/useRememberedState";
import useIsPhone from "../hooks/useIsPhone";
import { PhotoUploader } from "./PhotoUploader";
import { normalizeBusinessForm, assignmentFormPayload, isCivilDate, availableRefuelingCosts } from "../lib/businessValidation";
import { useBlocker, useBeforeUnload } from "react-router-dom";
import {randomId} from '../lib/randomId';
import VehicleColor from "./VehicleColor";

const CHECKLIST_ITEMS = ["carrozzeria", "pneumatici", "vetri", "interni", "documenti", "dotazioni", "chiavi"];

function refList(refData, type) {
  if (type === "vehicles") return refData.vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand} ${v.model}`, raw: v }));
  if (type === "suppliers") return refData.suppliers.map((s) => ({ value: s.id, label: s.name, raw: s }));
  if (type === "locations") return refData.locations.map((l) => ({ value: l.id, label: l.name, raw: l }));
  if (type === "assignees") return refData.assignees.map((a) => ({ value: a.id, label: a.name, raw: a }));
  return [];
}

function renderCell(row, col, refData) {
  const v = row[col.key];
  if (col.type === "currency") return fmtEUR(v);
  if (col.type === "date") return fmtDate(v);
  if (col.type === "badge") return v ? <StatusBadge status={v} kind={col.kind || "generic"} /> : "—";
  if (col.type === "deadline") return <DeadlineBadge expiry={row.expiry_date} />;
  if (col.type === "plate") return <span className="inline-flex items-center gap-2"><span className="font-mono font-semibold text-sky-300">{v || "—"}</span><VehicleColor vehicleId={row.vehicle_id} size="sm" /></span>;
  if (col.type === "labelize") return labelize(v);
  if (col.type === "ref") {
    const list = col.refType === "suppliers" ? refData.suppliers : col.refType === "locations" ? refData.locations : refData.assignees;
    const item = list.find((x) => x.id === v);
    return item ? item.name : "—";
  }
  return v ?? "—";
}

function rowIdentity(row) {
  return [row.vehicle_plate || row.name, row.date && fmtDate(row.date), row.description || row.category].filter(Boolean).join(' · ') || row.id;
}

function FormField({ field, value, onChange, refData, disabled = false, onBusyChange }) {
  const common = { id: field.key, "data-testid": `field-${field.key}` };
  if (field.type === "boolean") return <Checkbox {...common} aria-label={field.label} disabled={disabled} checked={value===true} onCheckedChange={checked=>onChange(field.key,checked===true)}/>;
  if (field.type === "textarea")
    return <Textarea {...common} disabled={disabled} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} rows={2} />;
  if (field.type === "checklist") {
    const cur = value || {};
    return (
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 sm:grid-cols-3">
        {CHECKLIST_ITEMS.map((it) => (
          <label key={it} className="flex items-center gap-2 text-sm capitalize">
            <Checkbox
              disabled={disabled}
              data-testid={`checklist-${it}`}
              checked={!!cur[it]}
              onCheckedChange={(c) => onChange(field.key, { ...cur, [it]: !!c })}
            />
            {it}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "photos")
    return <PhotoUploader value={value || []} onChange={(v) => onChange(field.key, v)} label="" disabled={disabled} onBusyChange={onBusyChange} />;
  if (field.type === "cost-link") {
    return (
      <Select value={value || "__unlinked__"} disabled={disabled} onValueChange={(val) => onChange(field.key, val === "__unlinked__" ? null : val)}>
        <SelectTrigger {...common} aria-label={field.label}><SelectValue placeholder="Nessun costo collegato" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="__unlinked__">Nessun costo collegato</SelectItem>
          {field.options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "ref" && field.refType === "assignees")
    return <ClientPicker id={field.key} testId={`field-${field.key}`} value={value || ""} disabled={disabled} onChange={(val, person) => onChange(field.key, val, field, person)} />;
  if (field.type === "select" || field.type === "ref") {
    const options = field.type === "ref" ? refList(refData, field.refType) : field.options.map((o) => ({ value: o.value, label: o.label }));
    return (
      <Select value={value || ""} disabled={disabled} onValueChange={(val) => onChange(field.key, val, field)}>
        <SelectTrigger {...common} aria-label={field.label}>
          <SelectValue placeholder="Seleziona…" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  const inputType = field.type === "number" || field.type === "currency" ? "number" : field.type === "date" ? "date" : field.type === "password" ? "password" : "text";
  return (
    <Input {...common} type={inputType} disabled={disabled} min={inputType === "number" ? 0 : undefined} step={field.type === "currency" ? "0.01" : field.type === "number" ? "any" : undefined}
      value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value)} />
  );
}

export default function CrudPage({ config }) {
  const { resource, title, icon: Icon, description, fields, columns, searchKeys, defaults } = config;
  const phone = useIsPhone();
  const { user, retryAuth } = useAuth();
  const refData = useRefData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useRememberedState(`${config.resource}:search`, "");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false), uploads = useRef(new Set()), createRequest = useRef(null);
  const deleteVersion=useRef('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const formBusy = saving || uploadBusy;
  const accessExitConfirmation=useAccountExitGuard({dirty:dialogOpen,busy:formBusy});
  const blocker = useBlocker(() => savingRef.current || uploads.current.size > 0);
  useBeforeUnload(useCallback(event => {
    if (savingRef.current || uploads.current.size) { event.preventDefault(); event.returnValue = ""; }
  }, []));
  useEffect(() => { if (blocker.state === "blocked" && !formBusy) blocker.reset(); }, [blocker, formBusy]);
  const onUploadBusy = (key, busy) => {
    if (busy) uploads.current.add(key); else uploads.current.delete(key);
    setUploadBusy(uploads.current.size > 0);
  };
  const [deleteId, setDeleteId] = useState(null);
  const [linkCosts, setLinkCosts] = useState([]);
  const [linkCostsLoading, setLinkCostsLoading] = useState(false);
  const [linkCostsError, setLinkCostsError] = useState("");
  const [linkCostsRetry, setLinkCostsRetry] = useState(0);

  const resourcePermission = resource === "assignments" ? "rentals" : resource;
  // Booking create/edit needs rentals.manage; handover steps live in the wizard.
  const canWrite = resource === "locations" ? canAdministerCompany(user) : can(user,`${resourcePermission}.manage`);
  const canCreate = canWrite && (user.active_site_id !== "all" || ["locations","suppliers"].includes(resource));
  const canArchive = canWrite && can(user,`${resourcePermission}.manage`);
  const canLinkCosts = can(user,"costs.read");
  const visibleFields = fields.filter(f=>(!f.immutableAfterCreate||!editing)&&(resource!=="assignments"||f.key!=="monthly_fee"||can(user,"practice_finance.manage"))&&(f.type!=="cost-link"||canLinkCosts));
  const formFields = resource === "assignments"
    ? visibleFields.filter((f) => !["status", "end_date_actual", "km_end", "fuel_end"].includes(f.key))
    : visibleFields;
  // A `section` on a field starts a group that runs until the next one; the
  // heading goes before the first field of the group that is actually shown.
  const sectionOf = useMemo(() => {
    let current = null;
    return Object.fromEntries(fields.map((f) => [f.key, (current = f.section || current)]));
  }, [fields]);

  useEffect(() => {
    if (!dialogOpen || resource !== "refuelings" || !canLinkCosts) return;
    let active = true;
    setLinkCostsLoading(true); setLinkCostsError(""); setLinkCosts([]);
    api.get("/costs").then(({ data }) => {
      if (!active) return;
      setLinkCosts(data);
      const reverseLink = editing && data.find((cost) => cost.refueling_id === editing.id);
      if (reverseLink && !Object.hasOwn(editing, "cost_id")) setForm((current) =>
        !Object.hasOwn(current, "cost_id") && current.vehicle_id === editing.vehicle_id ? { ...current, cost_id: reverseLink.id } : current);
    }).catch((e) => { if (active) setLinkCostsError(apiError(e)); })
      .finally(() => { if (active) setLinkCostsLoading(false); });
    return () => { active = false; };
  }, [dialogOpen, resource, canLinkCosts, editing, linkCostsRetry]);

  const costOptions = availableRefuelingCosts(linkCosts, items, form.vehicle_id, editing?.id).map((cost) => ({
    value: cost.id,
    label: `${isCivilDate(cost.date) ? fmtDate(cost.date) : "Data non disponibile"} · ${fmtEUR(cost.total)} · ${cost.description || labelize(cost.category)}`,
  }));
  if (form.cost_id && !costOptions.some((option) => option.value === form.cost_id)) {
    costOptions.push({ value: form.cost_id, label: "Costo collegato non presente tra quelli disponibili" });
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${resource}`);
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [resource]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    createRequest.current = randomId();
    setEditing(null);
    setForm({ ...(defaults || {}), ...(resource==="assignments"?{pickup_site_id:user.active_site_id,return_site_id:user.active_site_id}:{}) });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setDialogOpen(true);
  };

  const onChange = (key, val, field, person) => {
    setForm((f) => {
      const next = { ...f, [key]: val };
      if (field?.type === "ref" && field.refType === "vehicles") {
        const v = refData.vehicles.find((x) => x.id === val);
        if (v) next.vehicle_plate = v.plate;
        if (resource === "refuelings" && f.vehicle_id !== val) next.cost_id = null;
      }
      if (field?.type === "ref" && field.refType === "assignees") {
        // A customer created on the spot is not in the shared list yet: its name comes with it.
        const a = refData.assignees.find((x) => x.id === val) || person;
        if (a) next.assignee_name = a.name;
      }
      return next;
    });
  };

  const save = async () => {
    if (savingRef.current || uploads.current.size) return;
    let payload;
    try {
      payload = normalizeBusinessForm(form, formFields, resource);
      if (resource === "assignments") {payload = assignmentFormPayload(payload, editing);if(editing)delete payload.pickup_site_id;if(!can(user,"practice_finance.manage"))delete payload.monthly_fee;}
    } catch (e) { toast.error(e.message); return; }
    savingRef.current = true;setSaving(true);
    try {
      if (editing) {
        await api.put(`/${resource}/${editing.id}`, payload, {headers:{"If-Match":editing.updated_at || ""}});
        toast.success("Modifiche salvate");
      } else {
        await api.post(`/${resource}`, ["costs", "refuelings"].includes(resource) ? {...payload, request_id: createRequest.current} : payload);
        toast.success("Creato con successo");
      }
      setDialogOpen(false);
      await load();
      if (["suppliers", "locations", "assignees"].includes(resource)) refData.reload();
      if(resource === "locations") await retryAuth({background:true});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      savingRef.current = false;setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!canArchive) return;
    try {
      await api.delete(`/${resource}/${deleteId}`,{headers:{'If-Match':deleteVersion.current}});
      toast.success("Elemento archiviato");
      setDeleteId(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = useMemo(() => {
    let out = items;
    if (q) {
      const ql = q.toLowerCase();
      out = out.filter((r) => (searchKeys || Object.keys(r)).some((k) => String(r[k] ?? "").toLowerCase().includes(ql)));
    }
    if (sort.key) {
      const sortValue = columns.find((column) => column.key === sort.key)?.sortValue;
      out = [...out].sort((a, b) => {
        const av = sortValue ? sortValue(a) : a[sort.key] ?? "";
        const bv = sortValue ? sortValue(b) : b[sort.key] ?? "";
        return av > bv ? sort.dir : av < bv ? -sort.dir : 0;
      });
    }
    return out;
  }, [items, q, sort, searchKeys, columns]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  const rowActions = (row) => <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Modifica: ${rowIdentity(row)}`} onClick={() => openEdit(row)} data-testid={`edit-${row.id}`}><Pencil aria-hidden="true" className="h-4 w-4" /></Button>
                          {canArchive && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" aria-label={`Archivia: ${rowIdentity(row)}`} onClick={() => {deleteVersion.current=row.updated_at||'';setDeleteId(row.id);}} data-testid={`delete-${row.id}`}><Trash2 aria-hidden="true" className="h-4 w-4" /></Button>}
  </>;
  const emptyList = q ? <EmptyState icon={Search} title="Nessun risultato" description={`Nessun elemento corrisponde a “${q}”.`} action={<Button variant="outline" size="sm" onClick={() => setQ("")}>Cancella ricerca</Button>} /> : <EmptyState icon={Icon || Inbox} title="Nessun elemento registrato" description={canWrite && canCreate ? "Quando aggiungi il primo, lo trovi qui." : "Qui compariranno gli elementi registrati."} action={canWrite && canCreate && <Button size="sm" onClick={openCreate}><Plus className="mr-2 h-4 w-4" />Nuovo</Button>} />;
  return (
    <div className="space-y-5 animate-fade-up">{accessExitConfirmation}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {Icon && <div className="icon-tile h-11 w-11" aria-hidden="true"><Icon className="h-6 w-6" /></div>}
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
            {description && <p className="text-sm text-muted-foreground">{resource==="assignments"&&user.role==="driver"?"Consulta i veicoli assegnati a te, il periodo previsto e lo stato registrato.":description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {can(user,"export") && resource !== "locations" && <Button variant="outline" size="sm" disabled={!filtered.length} data-testid="print-btn" aria-label="Stampa" title="Stampa"
            onClick={() => printTable({ title, user, columns: columns.map((c) => ({ label: c.label, align: c.type === "currency" ? "right" : undefined })),
              rows: filtered.map((row) => columns.map((c) => printCell(row, c, refData))) })}>
            <Printer className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Stampa</span>
          </Button>}
          {can(user,"export") && resource !== "locations" && <Button variant="outline" size="sm" aria-label="Scarica CSV" title="Scarica CSV" onClick={() => downloadAuthorized(`/exports/${resource}`,`${resource}.csv`,{ids:filtered.map(row=>row.id).join(",")}).catch(e=>toast.error(apiError(e)))} disabled={loading||!filtered.length} data-testid="export-csv-btn">
            <Download className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">CSV</span>
          </Button>}
          {canWrite && (
            <Button size="sm" disabled={!canCreate} title={!canCreate?"Seleziona una singola sede per creare un record":undefined} onClick={openCreate} data-testid="add-btn">
              <Plus className="mr-2 h-4 w-4" /> Nuovo
            </Button>
          )}
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Label htmlFor={`${resource}-search`} className="sr-only">Cerca in {title}</Label>
        <Input id={`${resource}-search`} placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="search-input" />
      </div>

      <Card className="overflow-hidden border-border bg-card">
        {phone ? (
          // On a phone each row is a card: first column as title, the others as label and value.
          loading ? <ListSkeleton rows={4} label={`Caricamento ${title.toLowerCase()}…`} />
            : filtered.length === 0 ? emptyList
            : <ul className="divide-y divide-border" data-testid="phone-list">{filtered.map((row) => (
              <li key={row.id} className="space-y-3 p-4" data-testid={`row-${row.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 font-medium">{renderCell(row, columns[0], refData)}</div>
                  {canWrite && <div className="-mr-2 -mt-2 flex shrink-0 gap-1 [&_button]:h-10 [&_button]:w-10">{rowActions(row)}</div>}
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">{columns.slice(1).map((c) => (
                  <div key={c.key} className="min-w-0"><dt className="text-xs text-muted-foreground">{c.label}</dt><dd className="break-words">{renderCell(row, c, refData)}</dd></div>
                ))}</dl>
              </li>
            ))}</ul>
        ) : <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className="whitespace-nowrap" aria-sort={sort.key===c.key?(sort.dir===1?"ascending":"descending"):"none"}>
                    <button type="button" className="inline-flex min-h-8 items-center gap-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" onClick={()=>toggleSort(c.key)}>{c.label}<ArrowUpDown aria-hidden="true" className="h-3 w-3 opacity-40" /></button>
                  </TableHead>
                ))}
                {canWrite && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="hover:bg-transparent"><TableCell colSpan={columns.length + 1} className="p-0"><ListSkeleton rows={4} label={`Caricamento ${title.toLowerCase()}…`} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow className="hover:bg-transparent"><TableCell colSpan={columns.length + 1} className="p-0">{emptyList}</TableCell></TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} className="border-border" data-testid={`row-${row.id}`}>
                    {columns.map((c) => (<TableCell key={c.key} className="whitespace-nowrap">{renderCell(row, c, refData)}</TableCell>))}
                    {canWrite && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">{rowActions(row)}</div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>}
      </Card>
      <p className="text-sm text-muted-foreground">{filtered.length} elementi</p>

      <Dialog open={dialogOpen} onOpenChange={open => {if (!open && (savingRef.current || uploads.current.size)) return;setDialogOpen(open);}}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo"} — {title}</DialogTitle><DialogDescription>Compila i dati e premi Salva per confermare le modifiche.</DialogDescription></DialogHeader>
          {resource === "assignments" && (
            <div className="rounded-lg border border-border bg-secondary/20 p-3 text-sm">
              <div>Stato: <StatusBadge status={editing?.status || "pianificata"} /></div>
              {editing?<><RentalProgress assignment={editing} compact/>{canOpenRoute(user,'/consegne')&&['pianificata','in_consegna','in_corso'].includes(editing.status)&&<OperationalLink icon={ArrowLeftRight} className="mt-2" to={`/consegne?noleggio=${editing.id}`}>{editing.status==='in_corso'?'Gestisci riconsegna':'Prepara consegna'}</OperationalLink>}</>:<p className="mt-2 text-muted-foreground">Salva l’assegnazione per riservare il periodo. Potrai poi preparare e confermare la consegna.</p>}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            {formFields.map((f, index) => (
              <React.Fragment key={f.key}>
              {/* Long forms group their fields under headings; every field stays visible. */}
              {sectionOf[f.key] && sectionOf[f.key] !== sectionOf[formFields[index - 1]?.key] && <h3 className="border-b border-border pb-1.5 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground first:pt-0 sm:col-span-2">{sectionOf[f.key]}</h3>}
              <div className={f.full ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.key} className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-destructive" aria-hidden="true"> *</span>}</Label>
                <FormField field={f.type === "cost-link" ? { ...f, options: costOptions } : f} value={form[f.key]} onChange={onChange} refData={refData}
                  onBusyChange={busy => onUploadBusy(f.key, busy)}
                  disabled={saving || (resource === "assignments" && !!editing && editing.status !== "pianificata" && f.key === "vehicle_id") ||
                    (f.type === "cost-link" && (linkCostsLoading || !!linkCostsError || !form.vehicle_id))} />
                {f.type === "cost-link" && <div className="mt-2 text-xs text-muted-foreground">
                  <p>Se la spesa è già nei Costi, collegala qui: verrà conteggiata una sola volta nei report. Il rifornimento non genera una nuova voce nella pagina Costi.</p>
                  {!form.vehicle_id && <p className="mt-1">Seleziona prima il veicolo.</p>}
                  {linkCostsLoading && <p className="mt-1" role="status">Caricamento costi…</p>}
                  {linkCostsError && <p className="mt-1" role="alert">{linkCostsError} <button type="button" className="text-primary underline" onClick={() => setLinkCostsRetry((n) => n + 1)}>Riprova</button></p>}
                </div>}
              </div>
              </React.Fragment>
            ))}
          </div>
          <DialogFooter>
            {uploadBusy && <p role="status" className="text-sm text-muted-foreground">Attendi il caricamento degli allegati prima di salvare o uscire.</p>}
            <Button variant="outline" disabled={formBusy} onClick={() => setDialogOpen(false)}>Chiudi senza salvare</Button>
            <Button onClick={save} disabled={formBusy} data-testid="save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={blocker.state === "blocked"} onOpenChange={open => {if (!open) blocker.reset?.();}}>
        <AlertDialogContent><AlertDialogTitle>Operazione in corso</AlertDialogTitle><AlertDialogDescription>Attendi il completamento del caricamento o del salvataggio prima di uscire.</AlertDialogDescription><AlertDialogFooter><AlertDialogCancel onClick={() => blocker.reset?.()}>Resta nella scheda</AlertDialogCancel></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'archiviazione?</AlertDialogTitle>
            <AlertDialogDescription>L'elemento verrà archiviato (non eliminato definitivamente) e rimosso dagli elenchi attivi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">Archivia</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
